/* eslint-disable @typescript-eslint/prefer-for-of */
import { inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@scandium-oy/ngx-scandium';
import { combineLatest, Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { NewAdhocDialogComponent } from '../components/ad-hoc/new-dialog/new-adhoc.dialog';
import { CarouselItem } from '../components/carousel/item/carousel-item.component';
import { TicketCreatedDialogComponent } from '../components/ticket-created/ticket-created.dialog';
import { Contractor } from '../models/contractor.model';
import { Room } from '../models/room.model';
import { Site } from '../models/site.model';
import { SubItem } from '../models/sub-item.model';
import { RoomTicket, TicketHistory, TicketStatus } from '../models/ticket.model';
import { getUniqueGuid } from '../utility/list';
import { companyAdmins, companyManagers, Roles } from '../utility/role';
import { defaultRoom } from '../utility/room';
import { getStatusColor } from '../utility/ticket';
import { AreasService } from './areas.service';
import { ContractorsService } from './contractors.service';
import { SitesService } from './sites.service';
import { TicketsService } from './tickets.service';
import { UsersService } from './users.service';

export interface BasicTicket {
  site: Site;
  ticket: RoomTicket;
  contractor$?: Observable<Contractor>;
}

export interface TicketEdit {
  description?: boolean;
  other?: boolean;
}

export function getRoomNameByRoom(room: Room) {
  if (room == null) {
    return '';
  }
  let ret = '';
  if (room.siteArea && room.siteArea.name !== defaultRoom) {
    ret = `${room.siteArea.name} /`;
  }
  if (room.id != null && room.id !== defaultRoom) {
    ret += ` ${room.id}`;
  }
  return ret;
}

export function getChildAreaName(areaName: string) {
  if (areaName == null) {
    return '';
  }
  const ret = areaName.split('/');
  if (ret.length > 0) {
    return ret[ret.length - 1].trim();
  }
  return areaName;
}

@Injectable({ providedIn: 'root' })
export class WorkerTicketsService {
  private translate = inject(TranslateService);

  private currentUser$ = this.usersService.getCurrentUser();

  private userAreaTickets$ = this.contractorsService.getCurrentContractor().pipe(
    filter((contractor) => contractor != null),
    switchMap((contractor) => this.areasService.getAreaTickets(this.sites$, contractor.guid)),
    shareReplay(1),
  );

  private sites$ = this.sitesService.getActiveList().pipe(
    filter((sites) => sites?.length > 0),
    shareReplay(1),
  );

  private tickets$: Observable<BasicTicket[]> = combineLatest([this.userAreaTickets$, this.currentUser$, this.sites$, this.usersService.role$]).pipe(
    filter(([_, user]) => user != null),
    map(([areaTickets, _user, sites, _role]) =>
      areaTickets.map((ticket) => {
        const site = sites.find((s) => s.guid === ticket.site);
        return {
          site, ticket,
        };
      }),
    ),
    shareReplay(1),
  );

  created$ = this.tickets$.pipe(
    map((items) => items.map((it) => it.ticket)),
    shareReplay(1),
  );

  inProgress$ = this.tickets$.pipe(
    map((items) => items.map((it) => it.ticket)),
    shareReplay(1),
  );

  blocked$ = this.tickets$.pipe(
    map((items) => items.map((it) => it.ticket)),
    shareReplay(1),
  );

  paused$ = this.tickets$.pipe(
    map((items) => items.map((it) => it.ticket)),
    shareReplay(1),
  );

  constructor(
    private areasService: AreasService,
    private authService: AuthService,
    private contractorsService: ContractorsService,
    private modalCtrl: ModalController,
    private ticketsService: TicketsService,
    private sitesService: SitesService,
    private usersService: UsersService,
  ) { }

  private showToast(ticket: RoomTicket, site: Site) {
    this.modalCtrl.create({
      component: TicketCreatedDialogComponent,
      componentProps: {
        ticket,
      },
      cssClass: ['small-modal', 'stack-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data?.newTicket) {
          this.openSiteCalendarDialog({ site });
        }
      });
    });
  }

  private translateFields(item: RoomTicket) {
    if (item.name && item.nameText == null) {
      this.ticketsService.translateField(item.guid, 'name').pipe(
        take(1),
      ).subscribe();
    }
    if (item.history.some((h) => h.comment != null && h.commentText == null)) {
      this.ticketsService.translateField(item.guid, 'comment', true).pipe(
        take(1),
      ).subscribe();
    }
    if (item.notes?.some((n) => n.textText == null)) {
      this.ticketsService.translateField(item.guid, 'text', false, true).pipe(
        take(1),
      ).subscribe(() => {
        this.ticketsService.translateField(item.guid, 'title', false, true).pipe(
          take(1),
        ).subscribe();
      });
    }
    if (item.description && item.descriptionText == null) {
      this.ticketsService.translateField(item.guid, 'description').pipe(
        take(1),
      ).subscribe();
    }
  }

  getMyTickets(status?: TicketStatus, site?: string, onlyOwn = false) {
    const creator = this.usersService.currentUserS();
    const created$ = this.ticketsService.getList({ creator: creator.guid, site });
    if ([Roles.resident].includes(creator.role)) {
      const myAreas$ = this.areasService.getMyResidences(site).pipe(
        map((areas) => areas.map((area) => this.ticketsService.getList({ area: area.guid, site }).pipe(
          map((tickets) => tickets.filter((it) => area.access.includes(it.creator.guid))),
        ))),
        switchMap((tickets) => combineLatest(tickets)),
        map((tickets) => tickets.flat()),
      );
      return combineLatest([created$, myAreas$]).pipe(
        map(([createdTickets, areasTickets]) => [...createdTickets, ...areasTickets]),
        map((items) => items.filter((value, index, self) => index === self.findIndex((t) => t.guid === value.guid))),
      );
    } else if (companyAdmins.includes(creator.role)) {
      const client = creator.clients[0];
      const clients$ = this.ticketsService.getList({ client, status, site });
      return combineLatest([created$, clients$]).pipe(
        map(([createdTickets, clients]) => [...createdTickets, ...clients]),
        map((items) => items.filter((value, index, self) => index === self.findIndex((t) => t.guid === value.guid))),
      );
    } else if ([Roles.contractorAdmin, Roles.worker].includes(creator.role)) {
      const contractor = creator.contractors[0];
      const contractors$ = this.ticketsService.getList({ contractor, status, site });
      return combineLatest([created$, contractors$]).pipe(
        map(([createdTickets, contractors]) => [...createdTickets, ...contractors]),
        map((items) => items.filter((value, index, self) => index === self.findIndex((t) => t.guid === value.guid))),
        map((tickets) => tickets.filter((it) => !onlyOwn || it.users?.includes(creator.guid))),
      );
    } else if (companyManagers.includes(creator.role)) {
      const client = creator.clients[0];
      return this.sitesService.getMySites(creator.guid).pipe(
        switchMap((sites) => this.ticketsService.getList({ sites: sites.map((it) => it.guid), client })),
      );
    } else {
      return of<RoomTicket[]>([]);
    }
  }

  getCarouselItems(list: RoomTicket[]) {
    return list.map((it) => ({
      guid: it.guid,
      name: it.name,
      nameText: it.nameText,
      image: it.images?.length > 0 ? it.images[0] : null,
      preview$: it.preview$,
      date: it.deadline,
      parent: it,
      created: it.history[0].timestamp,
      label: this.translate.instant(`kanban.status.${it.status}`),
      labelColor: getStatusColor(it),
    }) as CarouselItem<RoomTicket>);
  }

  createHistory(
    status: TicketStatus | 'assigned' | 'joined' | 'removedBlocked',
    comment?: string,
    images?: string[],
    videos?: string[],
    username?: string,
  ) {
    const user = this.authService.getCurrentUser();
    const displayName = username ?? (this.usersService.currentUserS()?.displayName ?? user.displayName);
    const userGuid = username ? null : user.uid;
    const history: TicketHistory = {
      timestamp: new Date(),
      status,
      username: displayName,
      userGuid,
      images: images ?? [],
      videos: videos ?? [],
    };
    if (comment) {
      history.comment = comment;
    }
    return history;
  }

  cleanTicket(ticket: RoomTicket) {
    delete ticket.start;
    delete ticket.date;
    delete ticket.end;
    delete ticket.progress;
    delete ticket.preview$;
    return Object.assign({}, ticket);
  }

  filterBasicTickets(
    tickets: RoomTicket[], site: Site, text: string, creator: string, user: string, contractor: string, startDate: Date, endDate: Date,
  ) {
    const currentUser = this.usersService.currentUserS();
    return tickets
      .filter((ticket) => {
        if (site != null) {
          return ticket.site === site.guid;
        } else {
          return true;
        }
      })
      .filter((ticket) => {
        if (creator != null) {
          return ticket.creator.guid === creator;
        } else {
          return true;
        }
      })
      .filter((ticket) => {
        if (user != null) {
          return ticket.users.includes(user);
        } else {
          return true;
        }
      })
      .filter((ticket) => {
        if (contractor != null) {
          return ticket.contractor?.guid === contractor;
        } else {
          return true;
        }
      })
      .filter((ticket) => {
        if (text != null) {
          return ticket.name.toLowerCase().includes(text.toLowerCase())
            || ticket.creator.name.toLowerCase().includes(text.toLowerCase())
            || ticket.nameText?.[currentUser.lang ?? 'fi'].toLowerCase().includes(text.toLowerCase());
        } else {
          return true;
        }
      })
      .filter((ticket) => {
        if (startDate != null) {
          return ticket.date >= startDate;
        }
        if (endDate != null) {
          return ticket.date <= endDate;
        }
        return true;
      })
      .map((ticket) => {
        const clone = Object.assign({}, ticket);

        return clone;
      });
  }

  async saveRoomTickets(data: RoomTicket | RoomTicket[], site: Site, skipToast?: boolean) {
    if (Array.isArray(data)) {
      return Promise.all(data.map(async (it) => {
        return this.ticketsService.save(it).then((doc) => {
          it.guid = doc.id;
          this.translateFields(it);
          return doc;
        });
      })).then((docs) => docs.map((d) => d.id));
    } else {
      return this.ticketsService.save(data).then((doc) => {
        data.guid = doc.id;
        this.translateFields(data);
        if (!skipToast) {
          this.showToast(data, site);
        }
        return [doc.id];
      });
    }
  }

  async openSiteCalendarDialog(opts:
    {
      site: Site; ticket?: RoomTicket; sites?: Site[]; skipToast?: boolean;
    },
  ) {
    return this.modalCtrl.create({
      component: NewAdhocDialogComponent,
      componentProps: {
        site: opts.site, ticket: opts.ticket, sites: opts.sites,
      },
    }).then(async (m) => {
      m.present();
      return m.onDidDismiss<RoomTicket | RoomTicket[]>().then(async (data) => {
        if (data.data) {
          return this.saveRoomTickets(data.data, opts.site, opts.skipToast);
        }
        return [];
      });
    });
  }

  updateRoomTicket(roomTicket: RoomTicket | RoomTicket[]) {
    if (Array.isArray(roomTicket)) {
      for (let ticket of roomTicket) {
        this.ticketsService.update(this.cleanTicket(ticket)).then(() => this.translateFields(ticket));
      }
    } else {
      this.ticketsService.update(this.cleanTicket(roomTicket)).then(() => this.translateFields(roomTicket));
    }
  }

  createTicket(contractor: SubItem, site: Site, room: Room, name: string, index?: number) {
    const roomTicket: RoomTicket = {
      guid: getUniqueGuid(index),
      name: name,
      plainName: name,
      hours: 0,
      status: TicketStatus.created,
      history: [this.createHistory(TicketStatus.created, undefined, [], [])],
      users: [],
      usernames: [],
      area: { guid: room.siteArea.root, name: room.siteArea.name },
      images: [],
      videos: [],
      attachments: [],
      description: '',
      deadline: null,
      contractor,
      creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
      schedule: null,
      location: null,
      site: site.guid,
      client: site.client,
    };
    return roomTicket;
  }

  async deleteRoomTicket(ticket: RoomTicket) {
    return this.ticketsService.delete(ticket);
  }

  getTop(site: string) {
    return this.ticketsService.getTop(site);
  }
}
